import { preloadCharacterFigures } from "@/lib/location-scene-3d/character-humanoid"
import { createScene3dEngine } from "@/lib/location-scene-3d/engine"
import { platformIsSet } from "@/lib/location-scene-3d/platform"
import type { PlatformLayerState } from "@/lib/location-scene-3d/platform-layer"
import {
  ensurePrevizPlatform,
  normalizeSceneLayout,
  normalizeSceneLight,
  type SceneLayoutState,
} from "@/lib/location-scene-3d/scene-state"
import { fetchScene3d, uploadScene3dPoster } from "@/lib/library-api"

/** If a hero figure mesh is still loading, keep the room rather than wait forever. */
const MODEL_WAIT_MS = 2500

const posterCaptureInflight = new Map<number, Promise<void>>()

function waitFrames(count: number): Promise<void> {
  return new Promise((resolve) => {
    const step = (left: number) => {
      if (left <= 0) {
        resolve()
        return
      }
      requestAnimationFrame(() => step(left - 1))
    }
    step(count)
  })
}

function mountOffscreenStage(): { stage: HTMLDivElement; canvas: HTMLCanvasElement } {
  const stage = document.createElement("div")
  stage.setAttribute("aria-hidden", "true")
  stage.style.cssText =
    "position:fixed;left:-10000px;top:0;width:640px;height:360px;overflow:hidden;pointer-events:none;"
  const canvas = document.createElement("canvas")
  canvas.style.cssText = "display:block;width:100%;height:100%;"
  stage.appendChild(canvas)
  document.body.appendChild(stage)
  return { stage, canvas }
}

/**
 * Still of a saved scene without opening the editor, then replace the tile poster.
 * Best-effort: returns false on failure and does not throw.
 */
export async function captureAndUploadScene3dPoster(opts: {
  projectId: number
  scene3dId: number
  sceneState: Partial<SceneLayoutState> | null | undefined
  frameFormat?: string | null
}): Promise<boolean> {
  if (typeof document === "undefined") return false
  const layout = ensurePrevizPlatform(normalizeSceneLayout(opts.sceneState))
  const characterFigures = layout.objects.filter((obj) => obj.kind === "character").map((obj) => obj.figure)
  if (characterFigures.length > 0) {
    await Promise.race([
      preloadCharacterFigures(characterFigures),
      new Promise<void>((resolve) => {
        window.setTimeout(resolve, MODEL_WAIT_MS)
      }),
    ])
  }

  let stage: HTMLDivElement | null = null
  let engine: ReturnType<typeof createScene3dEngine> | null = null
  try {
    const mounted = mountOffscreenStage()
    stage = mounted.stage
    engine = createScene3dEngine({
      canvas: mounted.canvas,
      stage: mounted.stage,
      canEdit: false,
      canRecord: false,
      frameFormat: opts.frameFormat ?? null,
    })
    const platformVisual: PlatformLayerState = {
      platform: platformIsSet(layout.platform) ? layout.platform : null,
      cells: layout.cells ?? [],
      faces: layout.faces ?? [],
      openings: layout.openings ?? [],
      selectedSectors: [],
      selectedFaceTiles: [],
      activeFaceKey: null,
      light: normalizeSceneLight(layout.light),
      surfaceGrid: false,
    }
    engine.applySceneState(layout)
    engine.setPlatformState(platformVisual)
    engine.resize()
    await waitFrames(2)
    const blob = await engine.captureStillBlob({ includeLightMarker: true })
    if (!blob) return false
    await uploadScene3dPoster(opts.projectId, opts.scene3dId, blob)
    return true
  } catch {
    return false
  } finally {
    try {
      engine?.destroy()
    } catch {
      /* renderer already gone */
    }
    stage?.remove()
  }
}

/** Fetch the scene and upload a poster only when the tile has none yet. */
export function ensureScene3dPosterIfMissing(
  projectId: number,
  scene3dId: number,
  frameFormat?: string | null,
): Promise<void> {
  const existing = posterCaptureInflight.get(scene3dId)
  if (existing) return existing
  const job = (async () => {
    try {
      const detail = await fetchScene3d(projectId, scene3dId)
      if (detail.thumb_url || detail.poster_url) return
      await captureAndUploadScene3dPoster({
        projectId,
        scene3dId,
        sceneState: detail.scene_state ?? null,
        frameFormat,
      })
    } catch {
      /* best-effort cover; enter/exit still uploads */
    }
  })().finally(() => {
    posterCaptureInflight.delete(scene3dId)
  })
  posterCaptureInflight.set(scene3dId, job)
  return job
}
